import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const SESSION_STORAGE_KEY = 'arrowai_posthog_session';

let initialized = false;
let sessionListenersBound = false;
let sessionEnded = false;

const isBrowser = typeof window !== 'undefined';
const isEnabled = Boolean(POSTHOG_KEY);

const safeParseJson = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getSessionStorage = () => {
  if (!isBrowser) return null;
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const readStoredSession = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const parsed = safeParseJson(storage.getItem(SESSION_STORAGE_KEY));
  if (!parsed || !parsed.id || !parsed.startedAt) {
    return null;
  }
  return parsed;
};

const createSession = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  startedAt: Date.now(),
});

const persistSession = (session) => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

const clearSession = () => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem(SESSION_STORAGE_KEY);
};

const getActiveSession = () => {
  const existingSession = readStoredSession();
  if (existingSession) {
    return { session: existingSession, wasCreated: false };
  }

  const newSession = createSession();
  persistSession(newSession);
  return { session: newSession, wasCreated: true };
};

export const captureAnalyticsEvent = (eventName, properties = {}) => {
  if (!isEnabled) return;
  posthog.capture(eventName, properties);
};

const endSession = (reason) => {
  if (!isEnabled || sessionEnded) return;

  const session = readStoredSession();
  if (!session) return;

  sessionEnded = true;
  const durationSeconds = (Date.now() - session.startedAt) / 1000;
  captureAnalyticsEvent('app_session_ended', {
    session_id: session.id,
    duration_seconds: Number(durationSeconds.toFixed(2)),
    reason,
  });

  clearSession();
};

const bindSessionListeners = () => {
  if (!isBrowser || sessionListenersBound) return;
  sessionListenersBound = true;

  window.addEventListener('pagehide', () => endSession('pagehide'));
  window.addEventListener('beforeunload', () => endSession('beforeunload'));
};

const startSession = () => {
  if (!isEnabled) return;

  const { session, wasCreated } = getActiveSession();
  sessionEnded = false;

  if (wasCreated) {
    captureAnalyticsEvent('app_session_started', {
      session_id: session.id,
    });
  }
  bindSessionListeners();
};

export const initPostHog = () => {
  if (!isEnabled || !isBrowser || initialized) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    person_profiles: 'identified_only',
    persistence: 'localStorage+cookie',
  });

  initialized = true;
  startSession();
};

export const identifyPostHogUser = (user) => {
  if (!isEnabled || !user?.uid) return;

  posthog.identify(user.uid, {
    email: user.email || undefined,
    name: user.displayName || undefined,
  });
};

export const resetPostHogUser = () => {
  if (!isEnabled) return;
  posthog.reset();
};

export const capturePageView = (location) => {
  if (!isEnabled || !location) return;

  captureAnalyticsEvent('page_viewed', {
    path: location.pathname,
    search: location.search || '',
    hash: location.hash || '',
  });
};

export const isPostHogEnabled = isEnabled;
